import type * as zod from 'zod';

import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import * as log from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import { classifyDinoFast } from '../../vision/classifyDinoFast';
import type {
  ClassifyResult,
  RegionOfInterest,
} from '../../vision/VisionInterface';
import Step from '../Step';
import type { StepPlayArguments } from '../Step';

import Arguments from './Arguments';
import type {
  TemplateMatchClassifyMethod,
  DinoFastClassifyMethod,
} from './Arguments';
import Variables from './Variables';

const ns = log.namespace('classifyStep');

type Arguments = zod.infer<typeof Arguments>;

type Variables = zod.infer<typeof Variables>;

export default class ClassifyStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  private _reencodedEmbeddingCache = new Map<string, number[]>();

  public initializeVariableState(): void {
    this.variables = {
      latestResult: null,
    };
  }

  private async _captureFrame(): Promise<ArrayBuffer> {
    const { camera } = this.args;

    // Use type assertion since useCameraBotCpp may not be in the feature flag types yet
    const useCameraBotCpp = getFeatureFlagSync('useCppCameraBot', false);

    if (useCameraBotCpp) {
      if (!this.args.cameraEquipmentId) {
        throw new Error('cameraEquipmentId is required in C++ mode');
      }

      const cameraEquipment = this.routineContext.equipment.getDeviceById(
        this.args.cameraEquipmentId,
      ) as Camera | undefined;

      if (!cameraEquipment) {
        throw new Error(
          `Camera equipment with id ${this.args.cameraEquipmentId} not found`,
        );
      }

      return cameraEquipment.getColorFrame();
    }

    // Use old WristCamera implementation
    const wristCamera = this.routineContext.equipment.getWristCamera();

    if (!wristCamera) {
      throw new Error('No wrist camera found in Equipment');
    }

    if (!camera) {
      throw new Error('No camera integration provided');
    }

    return wristCamera.getColorFrame(camera);
  }

  private async _classifyTemplateMatch(
    method: zod.infer<typeof TemplateMatchClassifyMethod>,
    image: ArrayBuffer,
    regionOfInterest: zod.infer<typeof RegionOfInterest>,
  ): Promise<ClassifyResult[]> {
    return this.routineContext.vision.classify(
      method.classes,
      image,
      regionOfInterest,
    );
  }

  private async _classifyDinoFast(
    method: zod.infer<typeof DinoFastClassifyMethod>,
    image: ArrayBuffer,
    regionOfInterest: zod.infer<typeof RegionOfInterest>,
  ): Promise<ClassifyResult[]> {
    const { vision, equipment } = this.routineContext;

    return classifyDinoFast({
      vision,
      equipment,
      image,
      regionOfInterest,
      classes: method.classes,
      embeddingModelVersion: method.embeddingModelVersion,
      precision: method.precision,
      gpuTaskRunnerId: method.gpuTaskRunnerId,
      reencodeCache: this._reencodedEmbeddingCache,
    });
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    const { confidence, fallbackValue, regionOfInterest, method } = this.args;

    try {
      this.setVariable('latestResult', null);

      if (method.classes.length === 0) {
        throw new Error('No classes provided');
      }

      let image: ArrayBuffer;

      try {
        image = await this._captureFrame();
      } catch (cameraError) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: cameraError.message,
        });
      }

      let results: ClassifyResult[];

      switch (method.kind) {
        case 'TemplateMatch':
          results = await this._classifyTemplateMatch(
            method,
            image,
            regionOfInterest,
          );

          break;
        case 'DinoFastClassify':
          results = await this._classifyDinoFast(
            method,
            image,
            regionOfInterest,
          );

          break;
        default: {
          const exhaustive: never = method;
          const kind = (exhaustive as { kind?: string }).kind ?? 'unknown';

          throw new Error(`Unsupported classify method kind: ${kind}`);
        }
      }

      const value =
        !results[0] || results[0].score < confidence
          ? fallbackValue
          : results[0].name;

      log.info(ns`classify.value`, 'Classify result', value);

      if (await super.shouldStopPlaying()) {
        return;
      }

      this.setVariable('latestResult', value);
    } catch (error) {
      log.error(ns`classify.play`, 'Classify failed', error);

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Classify',
        },
        failureReason: `${error.message}`,
        error,
      });
    }
  }
}
